import type { EncounterAccessDestination } from 'common/lib/services/org/EncounterAccessService';
import { FILTER_METHODS } from 'src/utils/filters';
import { FilterQueryParams } from 'src/utils/types';

export interface OpenTarget {
    orgNameInternal: string;
    kind: 'encounters' | 'forms';
    id: number;
    encounterDestination: boolean;
}

export interface OpenDestination extends EncounterAccessDestination {
    organization: { orgId: number | string; orgNameInternal: string; orgName: string };
}

type LinkLocation = { pathname: string; search: string; hash?: string };
const encounterPath = /^\/([A-Za-z0-9_-]+)\/patients\/encounters$/;

export function isOpenLinkLocation(location: LinkLocation) {
    return (
        /^\/open(?:\/|$)/.test(location.pathname) ||
        (encounterPath.test(location.pathname) && new URLSearchParams(location.search).has('openEncounterId'))
    );
}

export function parseOpenLink(location: LinkLocation): OpenTarget | null {
    if (location.hash) return null;
    const mobile = /^\/open\/([A-Za-z0-9_-]+)\/(encounters|forms)\/([1-9][0-9]*)$/.exec(location.pathname);
    if (mobile && !location.search && Number.isSafeInteger(Number(mobile[3]))) {
        return {
            orgNameInternal: mobile[1],
            kind: mobile[2] as OpenTarget['kind'],
            id: Number(mobile[3]),
            encounterDestination: false,
        };
    }
    const encounter = encounterPath.exec(location.pathname);
    const params = new URLSearchParams(location.search);
    const id = params.get('openEncounterId') || '';
    if (
        encounter &&
        Array.from(params.keys()).length === 1 &&
        /^[1-9][0-9]*$/.test(id) &&
        Number.isSafeInteger(Number(id))
    ) {
        return { orgNameInternal: encounter[1], kind: 'encounters', id: Number(id), encounterDestination: true };
    }
    return null;
}

function validId(value: unknown) {
    return (
        (typeof value === 'number' || (typeof value === 'string' && /^[1-9][0-9]*$/.test(value))) &&
        Number.isSafeInteger(Number(value)) &&
        Number(value) > 0
    );
}

export function isValidDestination(value: OpenDestination, target: OpenTarget): boolean {
    return Boolean(
        value &&
            value.organization?.orgNameInternal === target.orgNameInternal &&
            validId(value.organization.orgId) &&
            validId(value.facility?.facilityId) &&
            validId(value.encounterId) &&
            (target.kind === 'forms'
                ? validId(value.encounterForm?.encounterFormId) &&
                  Number(value.encounterForm.encounterFormId) === target.id
                : Number(value.encounterId) === target.id &&
                  typeof value.encounterNumber === 'string' &&
                  value.encounterNumber.trim().length > 0),
    );
}

export function encounterLinkFilters(destination: OpenDestination, limit = 25): FilterQueryParams {
    return {
        query: '',
        orStatement: false,
        pagination: { page: 1, limit },
        filters: [
            {
                name: 'facId',
                method: FILTER_METHODS.Matches_Dropdown,
                data: { value1: Number(destination.facility.facilityId), value2: null },
            },
            {
                name: 'enctrNo',
                method: FILTER_METHODS.Equals_Text,
                data: { value1: destination.encounterNumber, value2: null },
            },
        ],
    };
}
